import { createStore } from 'zustand/vanilla';
import { FlowEngine } from '../engine/FlowEngine';
import { createEventBus } from './createEventBus';

export function createFlowStore({ flowRegistry, flowId, initialState }) {
    const engine = new FlowEngine({ flowRegistry, flowId, initialState });
    const events = createEventBus();

    const snapshotToState = (snapshot) => ({ ...snapshot });

    const store = createStore((set) => {
        const applyResult = (result, { remote = false } = {}) => {
            set(snapshotToState(result.snapshot));
            for (const ev of result.events) {
                events.emit({ ...ev, source: remote ? 'remote' : 'local' });
            }
            return result;
        };

        return {
            ...snapshotToState(engine.getSnapshot()),

            // Extra options (loopName/loopIndex, used by the continuous-flow view to
            // target a specific iteration) are forwarded to the engine; `remote` is
            // consumed here and only controls event tagging.
            setAttribute: (itemId, attribute, fieldIndex, value, options = {}) => {
                const { remote = false, ...engineOptions } = options;
                return applyResult(
                    engine.setAttribute(itemId, attribute, fieldIndex, value, engineOptions),
                    { remote },
                );
            },
            setApplyToAll: (itemId, value, { remote = false } = {}) =>
                applyResult(engine.setApplyToAll(itemId, value), { remote }),
            advance: ({ remote = false } = {}) =>
                applyResult(engine.advance(), { remote }),
            jumpTo: (args, { remote = false } = {}) =>
                applyResult(engine.jumpTo(args), { remote }),
            setLoopIteration: (loopName, index, options = {}, { remote = false } = {}) =>
                applyResult(engine.setLoopIteration(loopName, index, options), { remote }),
            setLoopPreliminary: (loopName, { remote = false } = {}) =>
                applyResult(engine.setLoopPreliminary(loopName), { remote }),
            changeFlow: (flowId, { remote = false } = {}) =>
                applyResult(engine.changeFlow(flowId), { remote }),
            validate: ({ remote = false } = {}) =>
                applyResult(engine.validate(), { remote }),
            reset: ({ remote = false } = {}) =>
                applyResult(engine.reset(), { remote }),
            restoreSnapshot: (state, { remote = false } = {}) =>
                applyResult(engine.hydrate(state), { remote }),
        };
    });

    // Expose the engine on the store itself (not as reactive state) so consumers
    // like SummaryList can call read-only methods (traverse) without subscribing
    // or leaking the engine into serializable snapshots.
    store.engine = engine;

    return { store, events };
}
